/**
 * Salesforce file queries — traverse Gobodo graph to fetch ContentDocuments
 * linked to a client, then download via the file-extractor Lambda.
 */

import { useQuery } from '@tanstack/react-query';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';

const customSuffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const CONTENT_DOC_ACCOUNT_REL = `content_document_account_rel${customSuffix}`;
const CONTENT_DOCUMENT_TYPE = `ContentDocument${customSuffix}`;

const FILE_EXTRACTOR_URL = process.env.NODE_ENV === 'development'
  ? '/file-extractor'
  : (process.env.REACT_APP_FILE_EXTRACTOR_URL ?? '').replace(/\/$/, '');

export interface SalesforceFile {
  contentVersionId: string;
  name: string;
  extension: string;
  size: number;
  updatedAt: string;
  source: 'salesforce';
}

async function fetchSalesforceFiles(clientId: string): Promise<SalesforceFile[]> {
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const graphId = GobodoConfig.graph();

  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(graphId);
  input.setStartObjectKey(clientId);
  input.setRelationshipTypes([CONTENT_DOC_ACCOUNT_REL]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([CONTENT_DOCUMENT_TYPE]);
  input.setMaxPathLength(1);
  input.setStartIndex(0);
  input.setCount(200);

  const results: GobodoApi.GraphObjectResponse[] = await traversalApi.traverseObjects(input);
  if (!Array.isArray(results)) return [];

  return results
    .map((obj): SalesforceFile | null => {
      const data = (obj as any).data ?? {};
      const contentVersionId: string = data.LatestPublishedVersionId__c ?? '';
      if (!contentVersionId) return null;
      return {
        contentVersionId,
        name: data.Title__c ?? (obj as any).label ?? 'Unnamed',
        extension: (data.FileExtension__c ?? '').toLowerCase(),
        size: Number(data.ContentSize__c ?? 0),
        updatedAt: data.ContentModifiedDate__c ?? (obj as any).modificationdate ?? '',
        source: 'salesforce',
      };
    })
    .filter((f): f is SalesforceFile => f !== null);
}

export function useSalesforceFiles(clientId: string, enabled = true) {
  return useQuery({
    queryKey: ['salesforce-files', clientId, customSuffix],
    queryFn: () => fetchSalesforceFiles(clientId),
    enabled: Boolean(USE_GOBODO_API && clientId && enabled),
    staleTime: 60_000,
  });
}

export async function downloadSalesforceFile(contentVersionId: string, fileName: string): Promise<void> {
  if (!FILE_EXTRACTOR_URL) {
    throw new Error('File extractor URL is not configured (REACT_APP_FILE_EXTRACTOR_URL)');
  }

  const response = await fetch(FILE_EXTRACTOR_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contentVersionId }),
  });

  if (!response.ok) {
    if (response.status === 404) throw new Error('The requested file could not be located or has not been synchronized yet. Please contact your administrator.');
    let message = `Download failed (HTTP ${response.status})`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
